import type { McpSearchScope, McpServer, McpTool, ToolGroup } from "./types";

export type PickerView =
  | { type: "servers" }
  | { type: "tools"; serverName: string }
  | { type: "review" };

export type VisibleSelectionState = {
  visibleToolFqns: string[];
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
};

export function deriveVisibleSelectionState({
  drillInTools,
  filteredServers,
  filteredToolGroups,
  hasSearch,
  searchScope,
  selected,
  view,
}: {
  /**
   * The open server's tools in the drill-in view, already narrowed by the
   * tools-view search. Empty outside the tools view. Passed in (not derived
   * here) so the same filtered list drives both this select-all math and what
   * the tools view renders — they can't disagree.
   */
  drillInTools: McpTool[];
  filteredServers: McpServer[];
  filteredToolGroups: ToolGroup[];
  hasSearch: boolean;
  searchScope: McpSearchScope;
  selected: Set<string>;
  view: PickerView;
}): VisibleSelectionState {
  const visibleToolFqns = getVisibleToolFqns({
    drillInTools,
    filteredServers,
    filteredToolGroups,
    hasSearch,
    searchScope,
    view,
  });
  let selectedVisibleCount = 0;
  for (const fqn of visibleToolFqns) {
    if (selected.has(fqn)) {
      selectedVisibleCount += 1;
    }
  }

  return {
    visibleToolFqns,
    allVisibleSelected:
      visibleToolFqns.length > 0 &&
      selectedVisibleCount === visibleToolFqns.length,
    someVisibleSelected: selectedVisibleCount > 0,
  };
}

function getVisibleToolFqns({
  drillInTools,
  filteredServers,
  filteredToolGroups,
  hasSearch,
  searchScope,
  view,
}: {
  drillInTools: McpTool[];
  filteredServers: McpServer[];
  filteredToolGroups: ToolGroup[];
  hasSearch: boolean;
  searchScope: McpSearchScope;
  view: PickerView;
}): string[] {
  if (view.type === "review") {
    return [];
  }
  // Drill-in shows the open server's (already-filtered) tools. This is
  // independent of the catalog query, which filters the servers list and stays
  // set so returning restores it.
  if (view.type === "tools") {
    return drillInTools.map((tool) => tool.qualified_name);
  }
  if (hasSearch && searchScope === "tools") {
    const visibleToolFqns: string[] = [];
    for (const group of filteredToolGroups) {
      for (const tool of group.tools) {
        visibleToolFqns.push(tool.qualified_name);
      }
    }
    return visibleToolFqns;
  }
  const visibleToolFqns: string[] = [];
  for (const server of filteredServers) {
    for (const tool of server.tools) {
      visibleToolFqns.push(tool.qualified_name);
    }
  }
  return visibleToolFqns;
}
